import { Grid } from '@mui/material';
import Button from '@mui/material/Button';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import FileUploadArea, { FileType } from 'app/components/FileUploadArea';
import { InputFieldFactory } from 'app/components/FormikField';
import { TranslationKeys } from 'app/translations';
import type { EmployerConnection } from 'app/types';

import { TypographyStyled } from './EmployeeFileUpload.styles';

type EmployeeFileUploadContentProps = {
  employerConnections: EmployerConnection[] | null;
  employeesTemplate: string;
  changeList: (files: any[]) => void;
  isSubmitting: boolean;
  fileErrors: any[];
  validating: boolean;
  progressMsg: string | null;
};

export const EmployeeFileUploadContent = ({
  employerConnections,
  employeesTemplate,
  changeList,
  isSubmitting,
  fileErrors,
  validating,
  progressMsg,
}: EmployeeFileUploadContentProps) => {
  const intl = useIntl();
  const field = useMemo(() => {
    const items = (employerConnections || []).map(connection => ({
      element: connection.connectionName,
      value: connection.connectionId,
      default: false,
    }));

    return employerConnections && employerConnections.length < 2
      ? {
          name: 'connectionId',
          type: 'autocomplete',
          required: true,
          header: 'onboarding.connectionStep',
          placeholder: intl.formatMessage({ id: 'onboarding.connectionStep' }),
          loading: false,
          displayEmpty: true,
          items,
        }
      : {
          type: 'multi-select',
          name: 'connectionId',
          label: intl.formatMessage({ id: TranslationKeys.onboarding_connectionStep }),
          displayEmpty: true,
          items,
        };
  }, [employerConnections, intl]);

  return (
    <Grid container spacing={2} justifyContent="center">
      <Grid item xs={4}>
        <InputFieldFactory field={field} />
      </Grid>
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button href={employeesTemplate} download="employees-template">
          <FormattedMessage id="employees.template.download" />
        </Button>
      </Grid>
      <Grid item xs={12}>
        <FileUploadArea
          types={[FileType.Excel]}
          onChange={changeList}
          disabled={false}
          showSuccessMessage={false}
          isSubmitting={isSubmitting}
        />
        {fileErrors.length > 0 &&
          !validating &&
          fileErrors.map(fileError => (
            <TypographyStyled key={`${fileError.message}-${fileError.values ?? 'file'}`} color="error" variant="body1">
              <FormattedMessage
                id={fileError.message}
                values={{ row: fileError?.values, b: chunks => <b>{chunks}</b>, br: <br /> }}
              />
            </TypographyStyled>
          ))}
        {progressMsg && (
          <TypographyStyled color="primary" variant="body1">
            <FormattedMessage id={progressMsg} />
          </TypographyStyled>
        )}
      </Grid>
    </Grid>
  );
};
